/**
 * Which page a hostname lands on.
 *
 * WHY THIS EXISTS: Firebase Hosting maps a custom domain to a SITE, not to a path. Every domain
 * pointed at this deployment therefore arrives at `/`, and there is no rewrite that can send one
 * of them to `/conflicts` instead — Hosting rewrites cannot branch on the request's host. The app
 * is a single-page app, so the same `index.html` answers every URL on every domain anyway; the
 * only place that knows which door somebody came through is the browser.
 *
 * WHY IT IS A CONSTANT RATHER THAN A SETTING. The repo's rule is that configuration lives in
 * Firestore, and this deliberately does not. A hostname cannot be added here alone and work:
 * it also needs a custom domain in Firebase Hosting, a DNS record, and a certificate. Putting it
 * in a settings document would offer an administrator a box that appears to do something and
 * cannot, which is worse than an entry that ships with the deploy that enables it.
 *
 * ADDING ONE: add the row here, add the domain in Firebase Hosting, and add the DNS record.
 * Note that AALRR's internal DNS answers `*.aalrr.com` with an unrelated host, so an explicit
 * internal CNAME is needed for each subdomain or the site resolves correctly outside the office
 * and not inside it.
 */

/** hostname (lower case, no port) -> the path its root should show. */
const LANDING_BY_HOST = {
  'conflicts.aalrr.com': '/conflicts',
  'candidateconflicts.aalrr.com': '/ccc',
};

/**
 * The path this host's root should land on, or null for the ordinary app.
 *
 * Only `/` is redirected. Every other path is left alone on purpose: somebody who follows a link
 * to a specific page on one of these domains should get that page, not be bounced to the door.
 *
 * @param {string} hostname  window.location.hostname
 * @param {string} pathname  window.location.pathname
 * @returns {string|null}
 */
export function landingPathForHost(hostname, pathname = '/') {
  if (pathname !== '/' && pathname !== '') return null;
  const host = String(hostname || '')
    .trim()
    .toLowerCase()
    .replace(/:\d+$/, '');
  const target = LANDING_BY_HOST[host] || null;
  // A host whose landing page IS the root would redirect to itself forever.
  return target && target !== pathname ? target : null;
}

export { LANDING_BY_HOST };
